import { AlertModal, Spinner, SpinnerColor } from '@lili/lili-frontend-common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Icon, Icons } from '@Components/icon/Icon';
import { useDeleteBusinessOwner } from '../api/ownersCenter.api';
import { getBusinessOwnerData } from '../api/util';
import { useOwnersCenterContext } from '../context/OwnersCenterContext';
import { useUboPercentageUpdate } from '../hooks/useUboPercentageUpdate';
import { OwnerPercentage } from './OwnerPercentage';
import type { UboResponse } from '@Store/index';
import type { OwnersCenterFormType } from '../OwnersCenterForm/OwnersCenterForm.types';

export interface UboItemProps {
  index: number;
  ubo: UboResponse;
  name: keyof OwnersCenterFormType;
}

export const UboItem = ({ index, ubo, name }: UboItemProps) => {
  const { onEditClick } = useOwnersCenterContext();
  const { handleUboPercentageChange, validateTotalPercentage } = useUboPercentageUpdate();
  const { t } = useTranslation();
  const { businessExternalId, isEmbedded, customerId } = getBusinessOwnerData();
  const { mutateAsync: deleteUbo, isPending: isDeletingUbo } = useDeleteBusinessOwner({
    businessExternalId: businessExternalId!,
    isEmbedded: isEmbedded,
    customerId: customerId!,
  });
  const [isDeleteWarningModalOpen, setIsDeleteWarningModalOpen] = useState<boolean>(false);
  const fullName = `${ubo.firstName} ${ubo.lastName}`;

  const showDeleteWarningModal = () => {
    setIsDeleteWarningModalOpen(true);
  };

  const deleteUboHandler = async () => {
    await deleteUbo(ubo.externalId);
    setIsDeleteWarningModalOpen(false);
  };

  const {
    formState: { errors },
  } = useFormContext<OwnersCenterFormType>();

  const hasFieldErrors = !!ubo.fieldValidationErrors?.length;
  const errorMessage = hasFieldErrors ? t('validation.missingDetails') : errors[name]?.message;
  return (
    <>
      <OwnerPercentage
        ownerName={fullName}
        ownerIndex={index + 2}
        name={name}
        canEdit
        errorMessage={errorMessage}
        handleOnBlur={() =>
          handleUboPercentageChange({
            fieldName: name,
          })
        }
        onChangeValidate={validateTotalPercentage}
        nameAccessoryComponent={
          <div className='flex gap-[8px] items-center'>
            <Icon
              icon={Icons.Edit}
              className={'text-outline-primary-bw-07 cursor-pointer'}
              onClick={() => onEditClick(ubo.externalId)}
            />
            {isDeletingUbo ? (
              <span className='w-[16px]'>
                <Spinner spinnerColor={SpinnerColor.Blue} height='16px' width='16px' />
              </span>
            ) : (
              <Icon
                icon={Icons.Delete}
                className={'text-outline-primary-bw-07 cursor-pointer'}
                onClick={showDeleteWarningModal}
              />
            )}
          </div>
        }
      />
      {isDeleteWarningModalOpen && (
        <AlertModal
          title={t('pages.ownersCenter.removeOwner')}
          description={t('pages.ownersCenter.removeOwnerConfirmation', { name: fullName })}
          submitButtonTitle={t('pages.ownersCenter.remove')}
          cancelButtonTitle={t('pages.ownersCenter.cancel')}
          onSubmit={deleteUboHandler}
          onCancel={() => setIsDeleteWarningModalOpen(false)}
        />
      )}
    </>
  );
};
